import {
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import {
    type NumericInputFractionDigitsConstantType,
    type NumericInputDecimalCountConstantType,
    type NumericInputStepperSizeConstantType,
    type NumericInputLimitTextConstantType,
    type NumericInputStepValueConstantType,
    type NumericInputSeparatorConstantType,
    type NumericInputKeyboardConstantType,
    type NumericInputSanitizeConstantType,
    type NumericInputFormatConstantType,
    type NumericInputColorConstantType,
    type NumericInputParseConstantType,
    type NumericInputJoinConstantType,
    type NumericInputTypeConstantType,
    type NumericInputDynamicStyleType,
    type NumericInputStepperMeasures,
    type NumericInputStepDirection,
    type NumericInputStepColors,
    type NumericInputKeyboard,
    type NumericInputType
} from "./type";
import type {
    Mutable
} from "../../types";
import type {
    FieldType
} from "../fieldControl/type";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    formatNumber
} from "../../helpers/locale";

export const NUMERIC_INPUT_TYPES: Record<NumericInputType, FieldType> = {
    question: "neutral",
    default: "neutral",
    neutral: "neutral",
    success: "success",
    warning: "warning",
    danger: "danger",
    info: "info"
};

export const NUMERIC_INPUT_STEP_SIGNS: Record<NumericInputStepDirection, number> = {
    decrement: -1,
    increment: 1
};

export const NUMERIC_INPUT_KEY_DIRECTIONS: Partial<Record<string, NumericInputStepDirection>> = {
    ArrowDown: "decrement",
    ArrowUp: "increment"
};

export const NUMERIC_INPUT_SEPARATOR_SAMPLE = 1.5;

export const NUMERIC_INPUT_DIGIT_PATTERN = /\d/;

export const getNumericInputType = ({
    type
}: NumericInputTypeConstantType): FieldType => {
    return NUMERIC_INPUT_TYPES[type];
};

export const getNumericInputKeyboard = ({
    isNegativeAllowed,
    keyboardType,
    isDecimal,
    inputMode
}: NumericInputKeyboardConstantType): NumericInputKeyboard => {
    if(keyboardType || inputMode) {
        return {
            keyboardType,
            inputMode
        };
    }

    if(isNegativeAllowed) {
        if(Platform.OS === "web") {
            return {
                inputMode: "text"
            };
        }

        return {
            keyboardType: Platform.OS === "ios" ? "numbers-and-punctuation" : "numeric"
        };
    }

    if(isDecimal) {
        return {
            keyboardType: "decimal-pad",
            inputMode: "decimal"
        };
    }

    return {
        keyboardType: "number-pad",
        inputMode: "numeric"
    };
};

export const joinNumericInputParts = ({
    separator,
    parts
}: NumericInputJoinConstantType): string | undefined => {
    const currentParts = parts.filter((part): part is string => !!part);

    if(!currentParts.length) {
        return undefined;
    }

    return currentParts.join(separator);
};

export const getNumericInputDecimalSeparator = ({
    locale
}: NumericInputSeparatorConstantType): string => {
    const sampleText = formatNumber({
        value: NUMERIC_INPUT_SEPARATOR_SAMPLE,
        locale
    });

    const separator = Array.from(sampleText).find((character) => !NUMERIC_INPUT_DIGIT_PATTERN.test(character));

    return separator ?? ".";
};

export const getNumericInputSanitizedText = ({
    isNegativeAllowed,
    decimalSeparator,
    isDecimal,
    maxLength,
    text
}: NumericInputSanitizeConstantType): string => {
    const groupSeparator = decimalSeparator === "," ? "." : ",";

    const isNegative = isNegativeAllowed && text.trim().startsWith("-");

    let normalizedText = text;

    if(isDecimal) {
        normalizedText = normalizedText.split(groupSeparator).join(normalizedText.includes(decimalSeparator) ? "" : decimalSeparator);
    }

    const characters = Array.from(normalizedText).filter((character) => NUMERIC_INPUT_DIGIT_PATTERN.test(character) || isDecimal && character === decimalSeparator);

    const [
        integerPart = "",
        ...fractionParts
    ] = characters.join("").split(decimalSeparator);

    const numberText = isDecimal && fractionParts.length ? `${integerPart || "0"}${decimalSeparator}${fractionParts.join("")}` : integerPart;

    const sanitizedText = `${isNegative ? "-" : ""}${numberText}`;

    if(maxLength === undefined) {
        return sanitizedText;
    }

    return sanitizedText.slice(0, maxLength);
};

export const parseNumericInputValue = ({
    isNegativeAllowed,
    decimalSeparator,
    isDecimal,
    text
}: NumericInputParseConstantType): number | undefined => {
    const sanitizedText = getNumericInputSanitizedText({
        isNegativeAllowed,
        decimalSeparator,
        isDecimal,
        text
    });

    if(!NUMERIC_INPUT_DIGIT_PATTERN.test(sanitizedText)) {
        return undefined;
    }

    const value = Number(sanitizedText.replace(decimalSeparator, "."));

    return Number.isFinite(value) ? value : undefined;
};

export const formatNumericInputValue = ({
    decimalSeparator,
    fractionDigits,
    value
}: NumericInputFormatConstantType): string => {
    return value.toFixed(fractionDigits).replace(".", decimalSeparator);
};

export const getNumericInputDecimalCount = ({
    value
}: NumericInputDecimalCountConstantType): number => {
    const [
        ,
        fractionText = ""
    ] = String(value).split(".");

    return fractionText.length;
};

export const getNumericInputFractionDigits = ({
    decimalSeparator,
    isDecimal,
    step,
    text
}: NumericInputFractionDigitsConstantType): number => {
    if(!isDecimal) {
        return 0;
    }

    const [
        ,
        fractionText = ""
    ] = getNumericInputSanitizedText({
        isNegativeAllowed: false,
        decimalSeparator,
        isDecimal,
        text
    }).split(decimalSeparator);

    return Math.max(fractionText.length, getNumericInputDecimalCount({
        value: step
    }));
};

export const getNumericInputStepValue = ({
    fractionDigits,
    direction,
    value,
    step,
    min,
    max
}: NumericInputStepValueConstantType): number => {
    const nextValue = (value ?? 0) + NUMERIC_INPUT_STEP_SIGNS[direction] * step;

    const lowerValue = min === undefined ? nextValue : Math.max(min, nextValue);

    const boundedValue = max === undefined ? lowerValue : Math.min(max, lowerValue);

    const safeValue = Math.min(Number.MAX_SAFE_INTEGER, Math.max(-Number.MAX_SAFE_INTEGER, boundedValue));

    return Number(safeValue.toFixed(fractionDigits));
};

export const getNumericInputLimitText = ({
    locale,
    prefix,
    suffix,
    value
}: NumericInputLimitTextConstantType): string => {
    const formattedValue = formatNumber({
        options: {
            maximumFractionDigits: getNumericInputDecimalCount({
                value
            })
        },
        locale,
        value
    });

    return `${prefix ?? ""}${formattedValue}${suffix ? ` ${suffix}` : ""}`;
};

export const getNumericInputStepperSize = ({
    touchTargets,
    currentSize,
    pointerType,
    radiuses,
    borders,
    spaces
}: NumericInputStepperSizeConstantType): NumericInputStepperMeasures => {
    const side = currentSize.minHeight - spaces.spacingSm;

    const hitSize = pointerType === "coarse" ? currentSize.minHeight : Math.max(side, touchTargets.fine);

    return {
        radius: concentric({
            inset: {
                vertical: (currentSize.minHeight - side) / 2,
                horizontal: spaces.spacingXs + borders.line
            },
            outer: radiuses.form,
            role: radiuses.form
        }),
        hitExtension: (hitSize - side) / 2,
        hitSize,
        side
    };
};

export const getNumericInputStepColors = ({
    isDisabled,
    colors
}: NumericInputColorConstantType): NumericInputStepColors => {
    if(isDisabled) {
        return {
            iconColor: colors.content.icon.disabled,
            iconColorKey: "disabled"
        };
    }

    return {
        iconColor: colors.content.icon.default,
        iconColorKey: "default"
    };
};

export const getNumericInputAffixColor = ({
    isDisabled,
    colors
}: NumericInputColorConstantType): string => {
    if(isDisabled) {
        return colors.system.state.content.disabled.neutral;
    }

    return colors.content.text.low;
};

const stylesheet = StyleSheet.create({
    input: {
        backgroundColor: "transparent",
        textAlignVertical: "center",
        includeFontPadding: false,
        alignSelf: "stretch",
        borderWidth: 0,
        minWidth: 0,
        padding: 0,
        margin: 0,
        flex: 1
    },
    stepperGroup: {
        flexDirection: "row",
        alignItems: "center"
    },
    stepperDivider: {
        alignSelf: "stretch"
    },
    affix: {
        fontVariant: [
            "tabular-nums"
        ],
        flexShrink: 0
    }
});

export const useStyles = ({
    currentSize,
    borders,
    colors,
    spaces
}: NumericInputDynamicStyleType) => {
    const styles = {
        stepperGroup: {
            marginEnd: spaces.spacingXs - currentSize.paddingHorizontal,
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        stepperDivider: {
            backgroundColor: colors.content.border.subtle,
            marginVertical: spaces.spacingSm,
            width: borders.line
        } as Mutable<ViewStyle>
    };

    return styles;
};

export default stylesheet;
